import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import Sidebar    from '../../component/DashboardUI/Sidebar';
import Topbar     from '../../component/DashboardUI/Topbar';
import { PulseDot, ProgressBar } from '../../component/DashboardUI/UI';
import { ThemeProvider } from '../../hooks/useTheme';
import { CONTESTANT, CONTESTANT_NAV, GENRES, CATEGORIES } from '../../data/mockData';
import '../../asset/css/main.css';

const RULES    = [['📁','Max 100 MB. Min 128 kbps. WAV/FLAC recommended.'],['🎵','Track: 1:00 – 8:00 minutes.'],['🆕','Original composition — no unauthorized samples.'],['🔒','Anonymous to judges until scoring ends.'],['🔁','Revise once within 48 hrs of submission.']];
const CRITERIA = [['🎼','Melody & Composition — 25 pts'],['🎛','Production Quality — 25 pts'],['💡','Originality — 25 pts'],['❤️','Emotional Impact — 25 pts']];
const TIPS     = [['✨','Master to −14 LUFS for commercial loudness.'],['🎯','Write a compelling note — judges read it post-scoring.'],['🖼','A strong cover image makes your entry memorable.'],['🏷','Add tags to appear in genre searches.']];
const EMPTY    = { title:'', genre:'', category:'', bpm:'', key:'', description:'', tags:'' };

export default function UploadTrack() {
  return (
    <ThemeProvider>
      <PageContent />
    </ThemeProvider>
  );
}

function PageContent() {
  const [sideOpen, setSideOpen] = useState(false);
  const [step,  setStep]  = useState(0);
  const [file,  setFile]  = useState(null);
  const [drag,  setDrag]  = useState(false);
  const [form,  setForm]  = useState(EMPTY);
  const fileRef = useRef();
  const navigate = useNavigate();

  const set      = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const pickFile = f => { if (f) { setFile(f); setStep(1); } };
  const reset    = () => { setFile(null); setForm(EMPTY); setStep(0); };
  const canSend  = file && form.title && form.genre && form.category;

  return (
    <div className="page-shell">
      <Sidebar
        navLinks={CONTESTANT_NAV}
        user={CONTESTANT}
        isJudge={false}
        isOpen={sideOpen}
        onClose={() => setSideOpen(false)}
      />

      <div className="page-body">
        <Topbar title="Upload Track" onMenuClick={() => setSideOpen(o => !o)} />

        <main className="page-content anim-up">

          {/* Step bar */}
          <div className="step-bar">
            {['Upload File', 'Track Details', 'Submitted!'].map((s, i) => (
              <div key={s} className={`step-item${i < step ? ' done' : i === step ? ' active' : ''}`}>
                <div className="step-num">{i < step ? '✓' : i + 1}</div>{s}
              </div>
            ))}
          </div>

          {/* Success */}
          {step === 2 ? (
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'55vh', textAlign:'center', gap:'1.35rem' }}>
              <div style={{ fontSize:'4.5rem' }}>🎉</div>
              <div style={{ fontFamily:"'Bebas Neue',cursive", fontSize:'2.8rem', letterSpacing:'0.05em', color:'var(--text)' }}>Track Submitted!</div>
              <div style={{ fontSize:'0.95rem', color:'var(--muted2)', maxWidth:430, lineHeight:1.7 }}>
                <strong style={{ color:'var(--text)' }}>{form.title || file?.name}</strong> is now in the judging queue. You'll be notified within 7–14 days.
              </div>
              <div className="badge badge-green" style={{ padding:'0.45rem 1.1rem', fontSize:'0.76rem' }}>
                <PulseDot /> Under Review
              </div>
              <button className="btn btn-red" onClick={reset}>Upload Another</button>
              <button className="btn btn-ghost" onClick={() => navigate('/contestant/dashboard')}>← Back to Dashboard</button>
            </div>
          ) : (
            /* Upload form */
            <div className="upload-layout">
              {/* Main form */}
              <div className="upload-main">
                {/* Dropzone */}
                {!file ? (
                  <div
                    className={`dropzone${drag ? ' drag' : ''}`}
                    onDragOver={e => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={e => { e.preventDefault(); setDrag(false); pickFile(e.dataTransfer.files[0]); }}
                    onClick={() => fileRef.current.click()}
                  >
                    <div className="dz-icon">🎵</div>
                    <h4>Drop Your Track Here</h4>
                    <p>Drag &amp; drop or click to browse. High-quality audio, up to 100 MB.</p>
                    <div className="fmt-row">
                      {['MP3','WAV','FLAC','AIFF','OGG'].map(f => <span className="fmt-tag" key={f}>{f}</span>)}
                    </div>
                    <input ref={fileRef} type="file" accept="audio/*" style={{ display:'none' }} onChange={e => pickFile(e.target.files[0])} />
                  </div>
                ) : (
                  <div className="file-prev">
                    <div className="file-prev-ico">🎧</div>
                    <div>
                      <div className="file-prev-name">{file.name}</div>
                      <div className="file-prev-size">{(file.size/1024/1024).toFixed(2)} MB · Audio file</div>
                    </div>
                    <button className="file-remove" onClick={() => { setFile(null); setStep(0); }}>✕</button>
                  </div>
                )}

                {/* Fields shown after file picked */}
                {step >= 1 && (
                  <>
                    <div className="fg">
                      <label className="flabel">Track Title *</label>
                      <input className="finput" placeholder="Give your track a name" value={form.title} onChange={set('title')} />
                    </div>
                    <div className="fg-row">
                      <div className="fg">
                        <label className="flabel">Genre *</label>
                        <select className="finput" value={form.genre} onChange={set('genre')}>
                          <option value="">Select genre</option>
                          {GENRES.map(g => <option key={g}>{g}</option>)}
                        </select>
                      </div>
                      <div className="fg">
                        <label className="flabel">Category *</label>
                        <select className="finput" value={form.category} onChange={set('category')}>
                          <option value="">Select category</option>
                          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="fg-row">
                      <div className="fg">
                        <label className="flabel">BPM (optional)</label>
                        <input className="finput" type="number" placeholder="e.g. 128" value={form.bpm} onChange={set('bpm')} />
                      </div>
                      <div className="fg">
                        <label className="flabel">Key (optional)</label>
                        <input className="finput" placeholder="e.g. A minor" value={form.key} onChange={set('key')} />
                      </div>
                    </div>
                    <div className="fg">
                      <label className="flabel">Artist Notes</label>
                      <textarea className="finput" placeholder="Tell judges about your track — inspiration, process, story…" value={form.description} onChange={set('description')} />
                    </div>
                    <div className="fg">
                      <label className="flabel">Tags (comma-separated)</label>
                      <input className="finput" placeholder="e.g. melodic, atmospheric, bass-heavy" value={form.tags} onChange={set('tags')} />
                    </div>
                    <div className="fg">
                      <label className="flabel">Cover Art (optional)</label>
                      <div style={{ border:'2px dashed var(--border2)', borderRadius:8, padding:'1.25rem', textAlign:'center', cursor:'pointer', color:'var(--muted2)', fontSize:'0.82rem', background:'var(--input-bg)' }}>
                        🖼 Click to upload cover image (JPG/PNG, max 5 MB)
                      </div>
                    </div>
                    <button
                      className="btn btn-red"
                      style={{ width:'100%', padding:'0.95rem', opacity: canSend ? 1 : 0.45 }}
                      onClick={() => canSend && setStep(2)}
                      disabled={!canSend}
                    >
                      🚀 Submit Entry
                    </button>
                  </>
                )}
              </div>

              {/* Aside */}
              <div className="upload-aside">
                <TipCard title="Submission Rules" rows={RULES} />
                <TipCard title="Judging Criteria" rows={CRITERIA}>
                  <div style={{ marginTop:'0.85rem', paddingTop:'0.85rem', borderTop:'1px solid var(--border)' }}>
                    <ProgressBar pct={100} color="linear-gradient(90deg,var(--red),var(--gold))" height={6} />
                    <div style={{ fontSize:'0.66rem', color:'var(--muted)', marginTop:4, textAlign:'right' }}>Total: 100 points</div>
                  </div>
                </TipCard>
                <TipCard title="Pro Tips" rows={TIPS} />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function TipCard({ title, rows, children }) {
  return (
    <div className="tip-card">
      <div className="tip-title">{title}</div>
      {rows.map(([ico, txt]) => (
        <div className="tip-row" key={txt}><span className="tip-ico">{ico}</span>{txt}</div>
      ))}
      {children}
    </div>
  );
}